import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Loader2, FileText, ChevronDown, ChevronRight, RefreshCw, Sparkles, AlertCircle, Layers,
} from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';

/**
 * QuickFindingsTab — Findings grouped by route, with multi-select for Change Orders.
 *
 * Replaces the verbose FindingsTab as the primary entry point. The user can:
 *   1. See all findings grouped by the page they came from
 *   2. Click into a finding to view its Mini-PRD (the verbatim quote + structured fields)
 *   3. Multi-select findings to "Generate Change Order" → consolidated brief for code agents
 */
export default function QuickFindingsTab() {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [openGroups, setOpenGroups] = useState({});
  const [selected, setSelected] = useState({});  // finding_id → bool
  const [previewFinding, setPreviewFinding] = useState(null);
  const [previewMd, setPreviewMd] = useState('');
  const [coDialog, setCoDialog] = useState({ open: false, title: '' });
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await tl.findingsGrouped();
      setGroups(data.groups || []);
      setTotal(data.total || 0);
      // Auto-expand groups on first load
      const exp = {};
      (data.groups || []).forEach((g) => { exp[g.route] = true; });
      setOpenGroups((prev) => Object.keys(prev).length ? prev : exp);
    } catch (e) {
      toast.error('Could not load findings: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected],
  );

  const toggleGroup = (route) => setOpenGroups({ ...openGroups, [route]: !openGroups[route] });
  const toggleAll = (route, checked) => {
    const g = groups.find((x) => x.route === route);
    if (!g) return;
    const next = { ...selected };
    g.findings.forEach((f) => { next[f.finding_id] = checked; });
    setSelected(next);
  };
  const toggleOne = (id, checked) => setSelected({ ...selected, [id]: checked });

  const previewMiniPRD = async (finding) => {
    setPreviewFinding(finding);
    setPreviewMd('');
    try {
      const r = await tl.findingMiniPRD(finding.finding_id);
      setPreviewMd(r.markdown);
    } catch (e) {
      toast.error('Could not load Mini-PRD: ' + (e.message || e));
    }
  };

  const generateChangeOrder = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one finding');
      return;
    }
    setGenerating(true);
    try {
      const co = await tl.generateChangeOrder({
        finding_ids: selectedIds,
        title: coDialog.title || null,
      });
      setCoDialog({ open: false, title: '' });
      setSelected({});
      toast.success(`Change Order ${co.change_order_id} generated`);
      window.location.href = `/testing-lab?tab=change_orders&co=${encodeURIComponent(co.change_order_id)}`;
    } catch (e) {
      toast.error('Generation failed: ' + (e.message || e));
    } finally {
      setGenerating(false);
    }
  };

  const copyMd = async () => {
    try {
      await navigator.clipboard.writeText(previewMd);
      toast.success('Mini-PRD copied');
    } catch { toast.error('Copy failed'); }
  };

  const downloadMd = () => {
    const blob = new Blob([previewMd], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${previewFinding?.finding_id || 'finding'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="quick-findings-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 sticky top-0 bg-background z-10 py-2 border-b">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs">{total} total</Badge>
          <Badge variant="outline" className="text-xs">{selectedIds.length} selected</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={load} data-testid="findings-reload">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setCoDialog({ open: true, title: '' })}
            disabled={selectedIds.length === 0}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="generate-co-btn"
          >
            <Layers className="w-3.5 h-3.5 mr-1.5" /> Generate Change Order ({selectedIds.length})
          </Button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground space-y-3" data-testid="findings-empty">
          <Sparkles className="w-10 h-10 mx-auto text-violet-500 opacity-50" />
          <div className="text-sm">No findings yet.</div>
          <p className="text-xs max-w-md mx-auto">
            Click <strong>Test this page</strong> on any page to start a Quick Review.
            Failures and pass-with-notes will appear here, grouped by page.
          </p>
        </div>
      ) : (
        groups.map((g) => {
          const allSelected = g.findings.every((f) => selected[f.finding_id]);
          const someSelected = !allSelected && g.findings.some((f) => selected[f.finding_id]);
          return (
            <Card key={g.route} className="p-0 overflow-hidden" data-testid={`finding-group-${g.route}`}>
              <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
                <button
                  onClick={() => toggleGroup(g.route)}
                  className="flex items-center gap-2 flex-1 text-left"
                  data-testid={`finding-group-toggle-${g.route}`}
                >
                  {openGroups[g.route] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <span className="font-mono text-xs">{g.route}</span>
                  <Badge variant="outline" className="text-xs">{g.count}</Badge>
                </button>
                <Checkbox
                  checked={allSelected}
                  data-state={someSelected ? 'indeterminate' : (allSelected ? 'checked' : 'unchecked')}
                  onCheckedChange={(c) => toggleAll(g.route, !!c)}
                  data-testid={`finding-group-selectall-${g.route}`}
                />
              </div>
              {openGroups[g.route] && (
                <div className="divide-y">
                  {g.findings.map((f) => {
                    const verdict = f.verdict || 'fail';
                    const cls = verdict === 'fail' ? 'text-red-600' : verdict === 'pass_with_note' ? 'text-amber-600' : 'text-emerald-600';
                    return (
                      <div key={f.finding_id} className="px-3 py-2 flex items-start gap-3 hover:bg-accent/40"
                           data-testid={`finding-row-${f.finding_id}`}>
                        <Checkbox
                          checked={!!selected[f.finding_id]}
                          onCheckedChange={(c) => toggleOne(f.finding_id, !!c)}
                          className="mt-1"
                          data-testid={`finding-check-${f.finding_id}`}
                        />
                        <button
                          onClick={() => previewMiniPRD(f)}
                          className="flex-1 text-left"
                          data-testid={`finding-open-${f.finding_id}`}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <Badge variant="outline" className={`text-[10px] ${cls}`}>
                              {verdict === 'fail' ? 'FAIL' : verdict === 'pass_with_note' ? 'PASS+NOTE' : 'PASS'}
                            </Badge>
                            <Badge variant="outline" className="text-[10px]">{f.severity}</Badge>
                            <span className="text-xs font-mono text-muted-foreground">{f.finding_id}</span>
                          </div>
                          <div className="text-sm">{f.title}</div>
                          {f.user_note_verbatim && (
                            <p className="text-xs italic text-muted-foreground mt-1 line-clamp-2">"{f.user_note_verbatim}"</p>
                          )}
                        </button>
                        <Button size="sm" variant="ghost" onClick={() => previewMiniPRD(f)} data-testid={`finding-prd-${f.finding_id}`}>
                          <FileText className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })
      )}

      {/* Mini-PRD viewer */}
      <Dialog open={!!previewFinding} onOpenChange={(o) => !o && setPreviewFinding(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="mini-prd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-violet-500" />
              Mini-PRD · {previewFinding?.finding_id}
            </DialogTitle>
            <DialogDescription className="text-xs">{previewFinding?.title}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 min-h-0">
            {previewMd ? (
              <pre className="text-xs whitespace-pre-wrap p-3 font-mono leading-relaxed bg-muted/30 rounded">{previewMd}</pre>
            ) : (
              <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="ghost" onClick={downloadMd} disabled={!previewMd}>Download .md</Button>
            <Button onClick={copyMd} disabled={!previewMd}>Copy markdown</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Change Order dialog */}
      <Dialog open={coDialog.open} onOpenChange={(o) => !o && setCoDialog({ open: false, title: '' })}>
        <DialogContent data-testid="co-generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate Change Order</DialogTitle>
            <DialogDescription>
              Aggregate {selectedIds.length} finding{selectedIds.length !== 1 ? 's' : ''} into a single Markdown change order ready to hand to your code agent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium">Title (optional)</label>
            <Input
              value={coDialog.title}
              onChange={(e) => setCoDialog({ ...coDialog, title: e.target.value })}
              placeholder="e.g. Tasks Kanban — first review pass"
              data-testid="co-title-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCoDialog({ open: false, title: '' })}>Cancel</Button>
            <Button onClick={generateChangeOrder} disabled={generating} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="co-confirm">
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Layers className="w-4 h-4 mr-1.5" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
